import { ModuleIcon, cn, LoadingSkeleton, ErrorState } from '@berky/ui'
import {
  ChartColumn,
  ChevronDown,
  House,
  Settings,
  MessageSquareText,
  Star,
} from 'lucide-react'
import { useLocation } from 'react-router'

import { NavigationLink, useNavigationItems } from './navigation-items'
import { useFeedbackUnreadCount } from '../feedback/management/api'
import type { NavigationGroup } from '../navigation/types'

export function NavigationPanel({ onNavigate }: { onNavigate: () => void }) {
  const {
    favoriteModules,
    groups,
    canManageUsers,
    canManageFeedback,
    isLoading,
    isError,
    retry,
  } = useNavigationItems()
  const unreadFeedback = useFeedbackUnreadCount(canManageFeedback)
  const location = useLocation()
  const showsAdministration = canManageUsers || canManageFeedback
  const unreadCount = unreadFeedback.data?.unreadCount ?? 0
  const currentSegment = location.pathname.split('/').filter(Boolean)[0]

  return (
    <nav
      id="platform-navigation-panel"
      aria-label="Hauptnavigation"
      data-testid="navigation-panel"
      className="absolute inset-x-0 top-full z-50 max-h-[calc(100svh-3rem)] overflow-y-auto border-b bg-card text-foreground shadow-lg motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-2 motion-safe:duration-200"
    >
      {isLoading ? (
        <LoadingSkeleton variant="text" rows={4} />
      ) : isError ? (
        <ErrorState
          description="Die Navigation konnte nicht geladen werden."
          onRetry={retry}
        />
      ) : (
        <>
          <div
            data-testid="navigation-desktop"
            className={cn(
              'hidden w-full gap-5 px-6 py-6 md:grid',
              showsAdministration ? 'xl:grid-cols-7' : 'xl:grid-cols-6',
              'md:grid-cols-3 lg:grid-cols-4',
            )}
          >
            <QuickAccessColumn
              headingId="navigation-quick-access-desktop"
              favoriteModules={favoriteModules}
              onNavigate={onNavigate}
            />

            {groups.map((group) => (
              <CategoryColumn
                key={group.category.id}
                group={group}
                onNavigate={onNavigate}
              />
            ))}

            {showsAdministration && (
              <AdministrationColumn
                canManageUsers={canManageUsers}
                canManageFeedback={canManageFeedback}
                unreadCount={unreadCount}
                onNavigate={onNavigate}
              />
            )}
          </div>

          <div data-testid="navigation-mobile" className="space-y-1 px-3 py-4 md:hidden">
            <QuickAccessColumn
              headingId="navigation-quick-access-mobile"
              favoriteModules={favoriteModules}
              onNavigate={onNavigate}
            />

            {groups.map((group) => (
              <details
                key={group.category.id}
                className="group border-t py-1"
                open={group.modules.some((module) => module.id === currentSegment)}
              >
                <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-md px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                  {group.category.title}
                  <ChevronDown
                    className="size-4 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <ModuleList modules={group.modules} onNavigate={onNavigate} />
              </details>
            ))}

            {showsAdministration && (
              <details
                className="group border-t py-1"
                open={[
                  'einstellungen',
                  'module-navigation',
                  'nutzungsanalyse',
                  'verwaltung',
                  'feedback',
                  'benachrichtigungen',
                ].includes(currentSegment ?? '')}
              >
                <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between rounded-md px-3 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                  Administration
                  <ChevronDown
                    className="size-4 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  />
                </summary>
                <AdministrationLinks
                  canManageUsers={canManageUsers}
                  canManageFeedback={canManageFeedback}
                  unreadCount={unreadCount}
                  onNavigate={onNavigate}
                />
              </details>
            )}
          </div>
        </>
      )}
    </nav>
  )
}

function ColumnHeading({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2
      id={id}
      className="px-3 pb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase"
    >
      {children}
    </h2>
  )
}

function QuickAccessColumn({
  headingId,
  favoriteModules,
  onNavigate,
}: {
  headingId: string
  favoriteModules: ReturnType<typeof useNavigationItems>['favoriteModules']
  onNavigate: () => void
}) {
  return (
    <section aria-labelledby={headingId} className="min-w-0">
      <ColumnHeading id={headingId}>Schnellzugriff</ColumnHeading>
      <ul className="space-y-1">
        <li>
          <NavigationLink to="/" label="Startseite" onNavigate={onNavigate} end>
            <House className="size-4 shrink-0" aria-hidden="true" />
          </NavigationLink>
        </li>
        {favoriteModules.map((module) => (
          <li key={module.id}>
            <NavigationLink
              to={`/${module.id}`}
              label={module.title}
              onNavigate={onNavigate}
            >
              <Star className="size-4 shrink-0 fill-current" aria-hidden="true" />
            </NavigationLink>
          </li>
        ))}
      </ul>
      {favoriteModules.length === 0 && (
        <p className="px-3 pt-2 text-xs leading-relaxed text-muted-foreground">
          Markieren Sie Anwendungen auf der Startseite mit einem Stern.
        </p>
      )}
    </section>
  )
}

function CategoryColumn({
  group,
  onNavigate,
}: {
  group: NavigationGroup
  onNavigate: () => void
}) {
  return (
    <section aria-labelledby={`navigation-${group.category.id}`} className="min-w-0">
      <ColumnHeading id={`navigation-${group.category.id}`}>
        {group.category.title}
      </ColumnHeading>
      <ModuleList modules={group.modules} onNavigate={onNavigate} />
    </section>
  )
}

function ModuleList({
  modules,
  onNavigate,
}: {
  modules: NavigationGroup['modules']
  onNavigate: () => void
}) {
  if (modules.length === 0) {
    return <p className="px-3 py-2 text-xs text-muted-foreground">Noch leer</p>
  }

  return (
    <ul className="space-y-1">
      {modules.map((module) => (
        <li key={module.id}>
          <NavigationLink
            to={`/${module.id}`}
            label={module.title}
            onNavigate={onNavigate}
          >
            <ModuleIcon name={module.icon} className="size-4 shrink-0" />
          </NavigationLink>
        </li>
      ))}
    </ul>
  )
}

function AdministrationColumn({
  canManageUsers,
  canManageFeedback,
  unreadCount,
  onNavigate,
}: {
  canManageUsers: boolean
  canManageFeedback: boolean
  unreadCount: number
  onNavigate: () => void
}) {
  return (
    <section aria-labelledby="navigation-administration" className="min-w-0">
      <ColumnHeading id="navigation-administration">Administration</ColumnHeading>
      <AdministrationLinks
        canManageUsers={canManageUsers}
        canManageFeedback={canManageFeedback}
        unreadCount={unreadCount}
        onNavigate={onNavigate}
      />
    </section>
  )
}

function AdministrationLinks({
  canManageUsers,
  canManageFeedback,
  unreadCount,
  onNavigate,
}: {
  canManageUsers: boolean
  canManageFeedback: boolean
  unreadCount: number
  onNavigate: () => void
}) {
  return (
    <ul className="space-y-1">
      {canManageUsers && (
        <>
          <li>
            <NavigationLink
              to="/einstellungen"
              label="Einstellungen"
              onNavigate={onNavigate}
            >
              <Settings className="size-4 shrink-0" aria-hidden="true" />
            </NavigationLink>
          </li>
          <li>
            <NavigationLink
              to="/nutzungsanalyse"
              label="Nutzungsanalyse"
              onNavigate={onNavigate}
            >
              <ChartColumn className="size-4 shrink-0" aria-hidden="true" />
            </NavigationLink>
          </li>
        </>
      )}
      {canManageFeedback && (
        <li>
          <NavigationLink
            to="/feedback"
            label="Feedback"
            onNavigate={onNavigate}
            trailing={
              unreadCount > 0 ? (
                <span
                  className="inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground"
                  aria-label={`${unreadCount} ungelesene Feedbacks`}
                >
                  {unreadCount}
                </span>
              ) : undefined
            }
          >
            <MessageSquareText className="size-4 shrink-0" aria-hidden="true" />
          </NavigationLink>
        </li>
      )}
    </ul>
  )
}
